import { BEIGE_500, GRAY_300, GRAY_600, GRAY_700, PAPER_WHITE } from 'constants/styling/theme'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { isServerSearchable, useSearchConversations } from 'dataQueries/conversations.query'
import {
  OrderSection,
  OverviewGroup,
  OverviewMode,
  StatusSection,
  getCategoryLabelKey,
  getOverviewGroup,
  groupByOrder,
  groupByStatus,
  matchesOverviewSearch,
  mergeSearchResults
} from '../../helpers/conversationOverview'

import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import { ConversationSummaryDTO } from 'models/conversations'
import { MUIInputField } from 'components/common/MUIInputField'
import { OverviewConversationCard } from './OverviewConversationCard'
import { OverviewGroupingToggle } from './OverviewGroupingToggle'
import { OverviewLoadMore } from './OverviewLoadMore'
import { OverviewSection } from './OverviewSection'
import SearchIcon from '@mui/icons-material/Search'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { flattenConversationPages } from 'dataQueries/conversationListCache'
import { getConversationDisplayTitle } from '../../helpers/conversationDisplay'
import { useDebouncedMemo } from 'utils/helpers/debouncedUseMemo'
import { useTranslation } from 'react-i18next'

interface Props {
  conversations: ConversationSummaryDTO[]
  currentUserId?: string
  selectedConversationId?: string | null
  onOpen: (conversationId: string) => void
  hasMoreConversations?: boolean
  isLoadingMoreConversations?: boolean
  isRefreshingConversations?: boolean
  onLoadMoreConversations?: () => void
}

interface SectionView {
  key: string
  title: string
  hint?: string
  collapsible: boolean
  defaultOpen: boolean
  items: ConversationSummaryDTO[]
}

const SEARCH_DEBOUNCE_MS = 300

const SLIDE_EASING = 'cubic-bezier(0.2, 0.8, 0.2, 1)'

const SLIDE_IN = {
  '@keyframes overview-slide-from-right': { from: { transform: 'translateX(100%)', opacity: 0 }, to: { transform: 'translateX(0)', opacity: 1 } },
  '@keyframes overview-slide-from-left': { from: { transform: 'translateX(-100%)', opacity: 0 }, to: { transform: 'translateX(0)', opacity: 1 } },
}

const ORDER_VIEW_MAX_PAGES = 25

const STATUS_SECTION_COPY: Record<OverviewGroup, { titleKey: string, hintKey?: string, collapsible: boolean, defaultOpen: boolean }> = {
  [OverviewGroup.REPLY_NEEDED]: { titleKey: 'chat:overview.reply_needed', hintKey: 'chat:overview.reply_needed_hint', collapsible: false, defaultOpen: true },
  [OverviewGroup.IN_PROGRESS]: { titleKey: 'chat:overview.in_progress_title', hintKey: 'chat:overview.in_progress_hint', collapsible: false, defaultOpen: true },
  [OverviewGroup.OTHER]: { titleKey: 'chat:overview.other_conversations', collapsible: true, defaultOpen: true },
  [OverviewGroup.RESOLVED]: { titleKey: 'chat:overview.resolved_cases', collapsible: true, defaultOpen: false },
}

export const ConversationOverview: FC<Props> = ({
  conversations,
  currentUserId,
  selectedConversationId,
  onOpen,
  hasMoreConversations = false,
  isLoadingMoreConversations = false,
  isRefreshingConversations = false,
  onLoadMoreConversations,
}) => {
  const { t } = useTranslation(['chat'])
  const [query, setQuery] = useState('')
  const [mode, setMode] = useState<OverviewMode>(OverviewMode.STATUS)
  const [hasSwitchedMode, setHasSwitchedMode] = useState(false)
  const [openOverrides, setOpenOverrides] = useState<Record<string, boolean>>({})
  const [isAutoLoadPaused, setIsAutoLoadPaused] = useState(false)
  const pendingLoadRef = useRef<{ visibleBefore: number, started: boolean } | null>(null)
  const trimmedQuery = query.trim()
  const isSearching = trimmedQuery.length > 0
  const debouncedQuery = useDebouncedMemo(() => trimmedQuery, [trimmedQuery], SEARCH_DEBOUNCE_MS)
  const scrollRef = useRef<HTMLDivElement>(null)
  const search = useSearchConversations(debouncedQuery)
  const searchMatches = useMemo(() => flattenConversationPages(search.data), [search.data])
  const isServerSearchActive = isServerSearchable(trimmedQuery)
  const isServerSearchCurrent = debouncedQuery === trimmedQuery && isServerSearchActive
  const serverMatches = isServerSearchCurrent && search.data ? searchMatches : undefined
  const isServerSearchPending = isServerSearchActive && (!isServerSearchCurrent || (search.isPending && !search.isError))
  const isServerSearchFailed = isServerSearchCurrent && search.isError && !search.data
  const loadMore = isServerSearchActive
    ? {
      hasMore: isServerSearchCurrent && !!search.hasNextPage && !search.isFetchNextPageError,
      isLoading: isServerSearchCurrent && search.isFetchingNextPage,
      load: () => void search.fetchNextPage(),
    }
    : {
      hasMore: hasMoreConversations && !!onLoadMoreConversations,
      isLoading: isLoadingMoreConversations,
      load: () => onLoadMoreConversations?.(),
    }

  const filtered = useMemo(
    () => {
      const localMatches = conversations.filter((conversation) => {
        const categoryLabelKey = getCategoryLabelKey(conversation)
        return matchesOverviewSearch(
          conversation,
          getConversationDisplayTitle(conversation, currentUserId, t),
          query,
          categoryLabelKey ? t(categoryLabelKey) : undefined
        )
      })
      return serverMatches ? mergeSearchResults(localMatches, serverMatches, conversations) : localMatches
    },
    [conversations, currentUserId, query, serverMatches, t]
  )

  const fromStatus = (section: StatusSection): SectionView => {
    const copy = STATUS_SECTION_COPY[section.group]
    return {
      key: section.group,
      title: t(copy.titleKey),
      hint: copy.hintKey ? t(copy.hintKey) : undefined,
      collapsible: copy.collapsible,
      defaultOpen: copy.defaultOpen,
      items: section.items,
    }
  }

  const fromOrder = (section: OrderSection): SectionView => {
    const order = section.order
    const orderLabel = order ? t('chat:overview.order', { number: order.number }) : undefined
    const needsReply = section.replyNeededCount > 0
      ? t('chat:overview.order_needs_reply', { count: section.replyNeededCount })
      : undefined
    return {
      key: order ? `order:${order.id}` : 'general',
      title: order ? (order.address ?? orderLabel ?? '') : t('chat:overview.general_questions'),
      hint: [
        order ? (order.address ? orderLabel : undefined) : t('chat:overview.general_questions_hint'),
        needsReply,
      ].filter(Boolean).join(' · ') || undefined,
      collapsible: true,
      defaultOpen: false,
      items: section.items,
    }
  }

  const sections: SectionView[] = mode === OverviewMode.STATUS
    ? groupByStatus(filtered, currentUserId).map(fromStatus)
    : groupByOrder(filtered, currentUserId).map(fromOrder)

  const isOpen = (section: SectionView) =>
    !section.collapsible || isSearching || (openOverrides[section.key] ?? section.defaultOpen)

  const visibleCount = sections.reduce((count, section) => count + (isOpen(section) ? section.items.length : 0), 0)

  const loadsEveryPage = mode === OverviewMode.ORDER && !isServerSearchActive
  const loadRef = useRef(loadMore.load)
  loadRef.current = loadMore.load
  const everyPageLoadsRef = useRef(0)

  useEffect(() => {
    everyPageLoadsRef.current = 0
  }, [mode])

  useEffect(() => {
    if (!loadsEveryPage || !loadMore.hasMore || loadMore.isLoading || isRefreshingConversations) return
    if (everyPageLoadsRef.current >= ORDER_VIEW_MAX_PAGES) return
    everyPageLoadsRef.current += 1
    loadRef.current()
  }, [loadsEveryPage, loadMore.hasMore, loadMore.isLoading, isRefreshingConversations])

  useEffect(() => {
    const pending = pendingLoadRef.current
    if (!pending) return
    if (loadMore.isLoading) {
      pending.started = true
      return
    }
    if (!pending.started) return
    pendingLoadRef.current = null
    if (visibleCount <= pending.visibleBefore) setIsAutoLoadPaused(true)
  }, [loadMore.isLoading, visibleCount])

  const loadNextPage = () => {
    pendingLoadRef.current = { visibleBefore: visibleCount, started: false }
    loadMore.load()
  }

  const resumeAutoLoad = () => {
    pendingLoadRef.current = null
    setIsAutoLoadPaused(false)
  }

  const switchMode = (next: OverviewMode) => {
    if (next === mode) return
    resumeAutoLoad()
    setHasSwitchedMode(true)
    setMode(next)
  }

  const slideAnimation = mode === OverviewMode.ORDER ? 'overview-slide-from-right' : 'overview-slide-from-left'

  const toggle = (section: SectionView) => {
    if (isSearching) return
    resumeAutoLoad()
    setOpenOverrides((current) => ({ ...current, [section.key]: !isOpen(section) }))
  }

  return (
    <Stack flex={1} minHeight={0}>

      <Stack gap="1.2rem" padding="0 1.6rem 1.2rem" sx={{ borderBottom: `1px solid ${GRAY_300}`, backgroundColor: PAPER_WHITE }}>

        <MUIInputField
          value={query}
          placeholder={t('chat:overview.search_placeholder')}
          startIcon={<SearchIcon sx={{ fontSize: '2rem', color: GRAY_700 }} />}
          inputAriaProps={{ 'aria-label': t('chat:overview.search_placeholder') }}
          onChange={(event) => {
            resumeAutoLoad()
            setQuery(event.target.value)
          }}
        />

        <OverviewGroupingToggle value={mode} onChange={switchMode} />

      </Stack>

      <Box ref={scrollRef} flex={1} minHeight={0} padding="1.2rem 1.6rem 2rem" sx={{ overflowY: 'auto', overflowX: 'hidden', backgroundColor: BEIGE_500 }}>

        <Stack
          key={mode}
          data-testid="overview-content"
          gap="2rem"
          sx={{
            ...SLIDE_IN,
            animation: hasSwitchedMode ? `${slideAnimation} 300ms ${SLIDE_EASING}` : 'none',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
          }}
        >

          {sections.length === 0 && !isServerSearchPending && !isServerSearchFailed &&
            <Typography variant="text-sm" color={GRAY_700} textAlign="center" margin="2.4rem 0.4rem">
              {t('chat:overview.no_results')}
            </Typography>
          }

          {sections.map((section) => (
            <OverviewSection
              key={section.key}
              title={section.title}
              hint={section.hint}
              count={section.items.length}
              collapsible={section.collapsible}
              isOpen={isOpen(section)}
              onToggle={() => toggle(section)}
            >
              {section.items.map((conversation) => (
                <OverviewConversationCard
                  key={conversation.id}
                  conversation={conversation}
                  currentUserId={currentUserId}
                  group={getOverviewGroup(conversation, currentUserId)}
                  mode={mode}
                  isSelected={selectedConversationId === conversation.id}
                  onClick={() => onOpen(conversation.id)}
                />
              ))}
            </OverviewSection>
          ))}

          {isServerSearchPending &&
            <Stack direction="row" alignItems="center" justifyContent="center" gap="0.8rem" padding="0.8rem 0" role="status" aria-label={t('chat:overview.searching')}>

              <CircularProgress size={16} sx={{ color: GRAY_600 }} />

              <Typography variant="text-sm" color={GRAY_700} fontSize="1.3rem">
                {t('chat:overview.searching')}
              </Typography>

            </Stack>
          }

          {isServerSearchFailed &&
            <Typography role="alert" variant="text-sm" color={GRAY_700} textAlign="center" fontSize="1.3rem">
              {t('chat:overview.search_failed')}
            </Typography>
          }

        </Stack>

        <OverviewLoadMore
          rootRef={scrollRef}
          hasMore={loadMore.hasMore && !isAutoLoadPaused}
          isLoading={loadMore.isLoading}
          onLoadMore={loadNextPage}
        />

      </Box>

    </Stack>
  )
}
